// ============================================================
//  SignupModal.jsx

import axios from "axios";
import { Eye, EyeOff, Lock, Mail, User, X } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import "../../../assets/css/customer/auth/Signup.css";
import AppContext from "../../../context/AppContext";

export default function SignupModal({ onClose, onSwitchToLogin }) {
  const { baseUrl, setLoggedInCustomer, RegExpChecker, setAuthToken } =
    useContext(AppContext);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createCustomer = () => {
    const fullNameErr = RegExpChecker("fullName", fullName);
    const emailErr = RegExpChecker("email", email);
    const passwordErr = RegExpChecker("password", password);

    // ── Confirm password validation ──────────────────────────
    let confirmPasswordErr = { confirmPassword: "" };
    if (!confirmPassword) {
      confirmPasswordErr = { confirmPassword: "Please confirm your password" };
    } else if (confirmPassword !== password) {
      confirmPasswordErr = { confirmPassword: "Passwords do not match" };
    }

    const errs = {
      ...fullNameErr,
      ...emailErr,
      ...passwordErr,
      ...confirmPasswordErr,
    };

    const hasErrors = Object.values(errs).some((msg) => msg !== "");
    if (hasErrors) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("email", email);
    formData.append("password", password);

    axios
      .post(`${baseUrl}/customer/auth/createCustomer`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data.success) {
          setLoggedInCustomer(response.data.customer);
          setAuthToken(response.data.customer.token);
          localStorage.setItem(
            "loggedInCustomer",
            JSON.stringify(response.data.customer),
          );
          localStorage.setItem("authToken", response.data.customer.token);

          setIsSubmitting(false);
          onClose();
        } else {
          setErrors({
            general:
              response.data.message || "Signup failed. Please try again.",
          });
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        console.log("Signup error:", error);

        setErrors({
          general:
            error.response?.data?.message ||
            "Something went wrong. Please try again.",
        });
      });
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <Container fluid>
      <div
        className="sm-overlay"
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-label="Sign Up"
      >
        <div className="sm-modal">
          {/* Close */}
          <button
            className="sm-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={14} strokeWidth={2.5} />
          </button>

          {/* Header */}
          <div className="sm-header">
            <>
              <h2 className="sm-title">Create an account</h2>
              <p className="sm-subtitle">Join us — it only takes a minute</p>
            </>
          </div>

          {/* Body */}
          <div className="sm-form-body">
            {/* General error */}
            {errors.general && (
              <div className="sm-general-error">⚠ {errors.general}</div>
            )}

            {/* Full Name */}
            <div className="sm-field">
              <label className="sm-label" htmlFor="sm-name">
                Full Name
              </label>
              <div className="sm-input-wrap">
                <span className="sm-input-icon">
                  <User size={16} />
                </span>
                <input
                  id="sm-name"
                  type="text"
                  className={`sm-input${errors.fullName ? " sm-input-error" : ""}`}
                  placeholder="Your Full name"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    setErrors(RegExpChecker("fullName", e.target.value));
                  }}
                  autoComplete="name"
                />
              </div>
              {errors.fullName && (
                <span className="sm-error-msg">⚠ {errors.fullName}</span>
              )}
            </div>

            {/* Email */}
            <div className="sm-field">
              <label className="sm-label" htmlFor="sm-email">
                Email
              </label>
              <div className="sm-input-wrap">
                <span className="sm-input-icon">
                  <Mail size={16} />
                </span>
                <input
                  id="sm-email"
                  type="email"
                  className={`sm-input${errors.email ? " sm-input-error" : ""}`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors(RegExpChecker("email", e.target.value));
                  }}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <span className="sm-error-msg">⚠ {errors.email}</span>
              )}
            </div>

            {/* Password */}
            <div className="sm-field">
              <label className="sm-label" htmlFor="sm-pw">
                Password
              </label>
              <div className="sm-input-wrap">
                <span className="sm-input-icon">
                  <Lock size={16} />
                </span>
                <input
                  id="sm-pw"
                  type={showPw ? "text" : "password"}
                  className={`sm-input${errors.password ? " sm-input-error" : ""}`}
                  placeholder="Min 8 characters"
                  value={password}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPassword(val);
                    setErrors((prev) => ({
                      ...prev,
                      ...RegExpChecker("password", val),
                      confirmPassword: confirmPassword
                        ? confirmPassword !== val
                          ? "Passwords do not match"
                          : ""
                        : prev.confirmPassword,
                    }));
                  }}
                  autoComplete="new-password"
                />
                <button
                  className="sm-eye-btn"
                  type="button"
                  onClick={() => setShowPw((p) => !p)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
              {errors.password && (
                <span className="sm-error-msg">⚠ {errors.password}</span>
              )}
            </div>

            {/* Confirm Password */}
            <div className="sm-field">
              <label className="sm-label" htmlFor="sm-cpw">
                Confirm Password
              </label>
              <div className="sm-input-wrap">
                <span className="sm-input-icon">
                  <Lock size={16} />
                </span>
                <input
                  id="sm-cpw"
                  type={showCpw ? "text" : "password"}
                  className={`sm-input${errors.confirmPassword ? " sm-input-error" : ""}`}
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    setConfirmPassword(val);
                    setErrors((prev) => ({
                      ...prev,
                      confirmPassword: val
                        ? val !== password
                          ? "Passwords do not match"
                          : ""
                        : prev.confirmPassword,
                    }));
                  }}
                  autoComplete="new-password"
                />
                <button
                  className="sm-eye-btn"
                  type="button"
                  onClick={() => setShowCpw((p) => !p)}
                  aria-label={
                    showCpw ? "Hide confirm password" : "Show confirm password"
                  }
                >
                  {showCpw ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="sm-error-msg">⚠ {errors.confirmPassword}</span>
              )}
            </div>

            {/* Submit */}
            <button
              className="sm-submit-btn"
              type="button"
              onClick={createCustomer}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating…" : "Create Account"}
            </button>

            {/* Switch */}
            <p className="sm-switch-text">
              Already have an account?{" "}
              <button
                className="sm-switch-link"
                type="button"
                onClick={onSwitchToLogin}
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </Container>
  );
}
